/**
 * HUG - Undo Context
 * Copyright (c) 2025 Underplay Music Ltd. All rights reserved.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

const UndoContext = createContext();

export const useUndo = () => {
  const context = useContext(UndoContext);
  if (!context) {
    throw new Error('useUndo must be used within UndoProvider');
  }
  return context;
};

export const UndoProvider = ({ children }) => {
  const [undoItem, setUndoItem] = useState(null);
  const [expiryTimer, setExpiryTimer] = useState(null);

  // Clear undo after 5 minutes
  useEffect(() => {
    if (undoItem) {
      const timer = setTimeout(() => {
        setUndoItem(null);
      }, 5 * 60 * 1000); // 5 minutes
      setExpiryTimer(timer);

      return () => clearTimeout(timer);
    }
  }, [undoItem]);

  const saveDeletedItem = (type, data) => {
    // Clear any existing timer
    if (expiryTimer) {
      clearTimeout(expiryTimer);
    }

    setUndoItem({
      type, // 'file', 'task', 'show', 'campaign', 'invoice', 'expense', 'artist', 'news'
      data,
      timestamp: Date.now(),
    });
  };

  const clearUndo = () => {
    if (expiryTimer) {
      clearTimeout(expiryTimer);
    }
    setUndoItem(null);
  };

  return (
    <UndoContext.Provider value={{ undoItem, saveDeletedItem, clearUndo }}>
      {children}
    </UndoContext.Provider>
  );
};
